/**
 * Axis spacing presets, lane solver, and axis label resolution.
 */

import { measureTextWidth, findCharsForWidth, measureLongestLabel } from '../textMeasure';

// ─── Clamp log deduplication ─────────────────────────────────────────────────
const clampLogDedupe = new Set();

export const DEFAULT_AXIS_SPACING = {
  xTickGap: 8,
  yTickGap: 7,
  xTickBaselineNudge: 2,
  xTickBaselineNudgeNumeric: 1,
  xTickBaselineNudgeCategorical: 2,
  yTickBaselineNudge: 3,
  xTickLength: 4,
  yTickLength: 4,
  minTitleOffset: 16,
  titleMinInset: 10,
  titleGapScale: 1.35,
  tickLabelLanePadX: 2,
  tickLabelLanePadY: 2,
};

export const AXIS_SPACING_PRESETS = {
  compact: {
    xTickBaselineNudge: 1,
    xTickBaselineNudgeNumeric: 0,
    xTickBaselineNudgeCategorical: 1,
    xTickGap: 7,
    yTickGap: 6,
    xTickLength: 3,
    yTickLength: 3,
    minTitleOffset: 14,
    titleMinInset: 8,
    titleGapScale: 1.15,
    tickLabelLanePadX: 1,
    tickLabelLanePadY: 1,
  },
  comfortable: {
    xTickBaselineNudge: DEFAULT_AXIS_SPACING.xTickBaselineNudge,
    xTickBaselineNudgeNumeric: DEFAULT_AXIS_SPACING.xTickBaselineNudgeNumeric,
    xTickBaselineNudgeCategorical: DEFAULT_AXIS_SPACING.xTickBaselineNudgeCategorical,
    xTickGap: DEFAULT_AXIS_SPACING.xTickGap,
    yTickGap: DEFAULT_AXIS_SPACING.yTickGap,
    xTickLength: DEFAULT_AXIS_SPACING.xTickLength,
    yTickLength: DEFAULT_AXIS_SPACING.yTickLength,
    minTitleOffset: DEFAULT_AXIS_SPACING.minTitleOffset,
    titleMinInset: DEFAULT_AXIS_SPACING.titleMinInset,
    titleGapScale: DEFAULT_AXIS_SPACING.titleGapScale,
    tickLabelLanePadX: DEFAULT_AXIS_SPACING.tickLabelLanePadX,
    tickLabelLanePadY: DEFAULT_AXIS_SPACING.tickLabelLanePadY,
  },
};

function getAxisSpacingPresetFromRuntime() {
  if (typeof window === 'undefined') return null;
  const queryPreset = new URLSearchParams(window.location.search).get('axisPreset');
  if (queryPreset) return queryPreset;
  return window.__COPLOT_AXIS_PRESET__ || null;
}

export function getAxisSpacingSpec(theme = {}) {
  const runtimePreset = getAxisSpacingPresetFromRuntime();
  const presetId = runtimePreset || theme.axisSpacingPreset || 'comfortable';
  const preset = AXIS_SPACING_PRESETS[presetId] || AXIS_SPACING_PRESETS.comfortable;
  return {
    gapX: theme.axisTickGapX ?? preset.xTickGap,
    gapY: theme.axisTickGapY ?? preset.yTickGap,
    xTickGap: theme.axisTickGapX ?? preset.xTickGap,
    yTickGap: theme.axisTickGapY ?? preset.yTickGap,
    xTickBaselineNudge: theme.axisTickBaselineNudgeX
      ?? theme.axisTickBaselineNudge
      ?? preset.xTickBaselineNudge
      ?? DEFAULT_AXIS_SPACING.xTickBaselineNudge,
    xTickBaselineNudgeNumeric: theme.axisTickBaselineNudgeXNumeric
      ?? theme.axisTickBaselineNudgeNumeric
      ?? preset.xTickBaselineNudgeNumeric
      ?? theme.axisTickBaselineNudgeX
      ?? theme.axisTickBaselineNudge
      ?? preset.xTickBaselineNudge
      ?? DEFAULT_AXIS_SPACING.xTickBaselineNudgeNumeric,
    xTickBaselineNudgeCategorical: theme.axisTickBaselineNudgeXCategorical
      ?? theme.axisTickBaselineNudgeCategorical
      ?? preset.xTickBaselineNudgeCategorical
      ?? theme.axisTickBaselineNudgeX
      ?? theme.axisTickBaselineNudge
      ?? preset.xTickBaselineNudge
      ?? DEFAULT_AXIS_SPACING.xTickBaselineNudgeCategorical,
    yTickBaselineNudge: theme.axisTickBaselineNudge ?? DEFAULT_AXIS_SPACING.yTickBaselineNudge,
    xTickLength: theme.axisTickLengthX ?? preset.xTickLength,
    yTickLength: theme.axisTickLengthY ?? preset.yTickLength,
    minTitleOffset: theme.axisTitleMinOffset ?? preset.minTitleOffset,
    titleMinInset: theme.axisTitleMinInset ?? preset.titleMinInset,
    titleGapScale: theme.axisTitleGapScale ?? preset.titleGapScale,
    tickLabelLanePadX: theme.axisTickLabelLanePadX ?? preset.tickLabelLanePadX,
    tickLabelLanePadY: theme.axisTickLabelLanePadY ?? preset.tickLabelLanePadY,
    presetId,
  };
}

export function getXAxisBaselineNudge({
  tickType = 'categorical',
  spacingSpec = {},
}) {
  if (tickType === 'numeric') {
    return spacingSpec.xTickBaselineNudgeNumeric ?? spacingSpec.xTickBaselineNudge ?? DEFAULT_AXIS_SPACING.xTickBaselineNudgeNumeric;
  }
  if (tickType === 'categorical') {
    return spacingSpec.xTickBaselineNudgeCategorical ?? spacingSpec.xTickBaselineNudge ?? DEFAULT_AXIS_SPACING.xTickBaselineNudgeCategorical;
  }
  return spacingSpec.xTickBaselineNudge ?? DEFAULT_AXIS_SPACING.xTickBaselineNudge;
}

export function isAxisDebugEnabled() {
  if (typeof window === 'undefined') return false;
  const globalFlag = Boolean(window.__COPLOT_AXIS_DEBUG__);
  const queryFlag = new URLSearchParams(window.location.search).get('axisDebug') === '1';
  return globalFlag || queryFlag;
}

export function reportAxisLaneClamp(meta = {}) {
  if (process.env.NODE_ENV === 'production') return;
  if (!meta || !meta.clamped) return;
  const {
    chartId = 'unknown-chart',
    axisSide = 'unknown-axis',
    viewportWidth = 'na',
    viewportHeight = 'na',
    rawTitleOffsetPx = 'na',
    titleOffsetPx = 'na',
    requiredMarginPx = 'na',
    availableMargin = 'na',
  } = meta;
  const dedupeKey = [
    chartId,
    axisSide,
    viewportWidth,
    viewportHeight,
    Math.round(Number(rawTitleOffsetPx) || 0),
    Math.round(Number(titleOffsetPx) || 0),
    Math.round(Number(requiredMarginPx) || 0),
    Math.round(Number(availableMargin) || 0),
  ].join('|');
  if (clampLogDedupe.has(dedupeKey)) return;
  clampLogDedupe.add(dedupeKey);
  // eslint-disable-next-line no-console
  console.warn('[axis-lanes] clamped axis lane', {
    chartId,
    axisSide,
    viewportWidth,
    viewportHeight,
    rawTitleOffsetPx,
    titleOffsetPx,
    requiredMarginPx,
    availableMargin,
    showTicks: meta.showTicks,
    tickLabelLaneExtent: meta.tickLabelLaneExtent,
    tickLabelOffsetPx: meta.tickLabelOffsetPx,
  });
}

/** Approximate px width of a string at a given font size. */
export function estimateTextWidth(text, fontSize, fontFamily = 'sans-serif', fontWeight = 'normal') {
  return measureTextWidth(text, { fontSize, fontFamily, fontWeight });
}

/** Truncate a label with ellipsis if it exceeds maxChars. */
export function truncateLabel(text, maxChars = 12) {
  const s = String(text);
  return s.length > maxChars ? s.slice(0, maxChars - 1) + '\u2026' : s;
}

// ─── X-axis label system ─────────────────────────────────────────────────────

const LABEL_HEIGHT_RATIO = 0.4;
const MIN_READABLE_FONT = 9;
const FONT_LINE_HEIGHT = 1.2;

function rotatedLabelExtent(textWidthPx, fontSize, rotationDeg) {
  if (!rotationDeg) return fontSize * FONT_LINE_HEIGHT;
  const rad = (rotationDeg * Math.PI) / 180;
  return textWidthPx * Math.sin(rad) + fontSize * Math.cos(rad);
}

function rotatedLabelFootprint(textWidthPx, fontSize, rotationDeg) {
  if (!rotationDeg) return textWidthPx;
  const rad = (rotationDeg * Math.PI) / 180;
  return textWidthPx * Math.cos(rad) + fontSize * Math.sin(rad);
}

/**
 * Custom tick renderer for visx AxisBottom that handles rotation correctly.
 */
export function makeTickComponent(rotation, { fontSize = 13, fill = '#000', fontFamily, tickGap = 0 } = {}) {
  return ({ x, y, formattedValue }) => {
    if (!rotation || rotation === 0) {
      return (
        <text
          x={x}
          y={y + tickGap}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fontSize}
          fill={fill}
          fontFamily={fontFamily}
        >
          {formattedValue}
        </text>
      );
    }
    const ty = y + tickGap;
    return (
      <text
        x={x}
        y={ty}
        textAnchor="end"
        fontSize={fontSize}
        fill={fill}
        fontFamily={fontFamily}
        transform={`rotate(-${rotation}, ${x}, ${ty})`}
      >
        {formattedValue}
      </text>
    );
  };
}

export function getRotatedTickLabelProps(rotation) {
  if (!rotation || rotation === 0) {
    return { textAnchor: 'middle', dominantBaseline: 'hanging' };
  }
  return { textAnchor: 'end', dominantBaseline: 'auto' };
}

function computeRotationStrategy(labels, availableWidth, baseFontSize, fontFamily) {
  const count = labels.length;
  if (count === 0) return { rotation: 0, fontSize: baseFontSize, slotWidth: availableWidth };

  const slotWidth = availableWidth / count;
  const fontOpts = { fontSize: baseFontSize, fontFamily };
  const longest = measureLongestLabel(labels, fontOpts);

  if (longest.width <= slotWidth - 4) {
    return { rotation: 0, fontSize: baseFontSize, slotWidth };
  }

  const footprint45 = rotatedLabelFootprint(longest.width, baseFontSize, 45);
  if (footprint45 <= slotWidth) {
    return { rotation: 45, fontSize: baseFontSize, slotWidth };
  }

  const font45 = Math.min(baseFontSize, Math.max(MIN_READABLE_FONT, Math.floor(slotWidth * 0.95)));
  if (font45 >= MIN_READABLE_FONT) {
    return { rotation: 45, fontSize: font45, slotWidth };
  }

  return { rotation: 45, fontSize: MIN_READABLE_FONT, slotWidth };
}

export function resolveXAxisLabels({
  labels,
  chartWidth,
  chartHeight,
  baseFontSize = 13,
  fontFamily = 'sans-serif',
  rotationOverride = null,
  truncateLabels = false,
  estimatedMargins = 150,
}) {
  const count = labels.length;
  if (count === 0) {
    return {
      rotation: 0,
      fontSize: baseFontSize,
      tickValues: [],
      tickFormat: (v) => String(v),
      tickLabelProps: getRotatedTickLabelProps(0),
      fontFamily,
      labelExtent: baseFontSize * FONT_LINE_HEIGHT,
      tickInterval: 1,
    };
  }

  const availableWidth = Math.max(120, chartWidth - estimatedMargins);
  const strategy = computeRotationStrategy(labels, availableWidth, baseFontSize, fontFamily);
  const rotation = rotationOverride ?? strategy.rotation;
  const fontSize = strategy.fontSize;
  const fontOpts = { fontSize, fontFamily };

  const longest = measureLongestLabel(labels, fontOpts);
  const fullLabelExtent = rotatedLabelExtent(longest.width, fontSize, rotation);

  const slotWidth = availableWidth / count;
  let tickInterval = 1;
  if (rotation === 45 && fontSize <= MIN_READABLE_FONT && slotWidth < fontSize * 0.95) {
    tickInterval = Math.max(1, Math.ceil((fontSize * 0.95) / slotWidth));
  }
  const tickValues = tickInterval > 1
    ? labels.filter((_, i) => i % tickInterval === 0)
    : labels;

  const USER_TRUNCATE_MAX_CHARS = 10;
  const maxExtent = chartHeight * LABEL_HEIGHT_RATIO;
  const mustTruncate = fullLabelExtent > maxExtent;

  let tickFormat = (val) => String(val);
  let labelExtent = fullLabelExtent;

  if (truncateLabels) {
    tickFormat = (val) => truncateLabel(val, USER_TRUNCATE_MAX_CHARS);
    const truncWidth = measureTextWidth(truncateLabel(longest.text, USER_TRUNCATE_MAX_CHARS), fontOpts);
    labelExtent = rotatedLabelExtent(truncWidth, fontSize, rotation);
  } else if (mustTruncate && rotation) {
    const rad = (rotation * Math.PI) / 180;
    const sinR = Math.sin(rad);
    const cosR = Math.cos(rad);
    const targetTextWidth = sinR > 0.01
      ? (maxExtent - fontSize * cosR) / sinR
      : longest.width;
    const maxChars = Math.max(4, findCharsForWidth(longest.text, Math.max(20, targetTextWidth), fontOpts));
    tickFormat = (val) => truncateLabel(val, maxChars);
    labelExtent = Math.min(fullLabelExtent, maxExtent);
  } else if (mustTruncate) {
    const maxChars = Math.max(4, findCharsForWidth(longest.text, Math.max(20, slotWidth - 4), fontOpts));
    tickFormat = (val) => truncateLabel(val, maxChars);
  }

  const tickLabelProps = getRotatedTickLabelProps(rotation);

  return {
    rotation,
    fontSize,
    fontFamily,
    tickValues,
    tickFormat,
    tickLabelProps,
    labelExtent,
    tickInterval,
  };
}

export function resolveYAxisLabels({
  labels,
  innerHeight,
  baseFontSize = 13,
  fontFamily = 'sans-serif',
  truncateLabels = false,
}) {
  const count = labels.length;
  if (count === 0) {
    return {
      tickValues: [],
      tickFormat: (v) => String(v),
      fontSize: baseFontSize,
      tickInterval: 1,
    };
  }

  const FONT_LINE_HT = 1.35;
  const MIN_FONT = 9;
  const slotHeight = innerHeight / count;

  let fontSize = baseFontSize;
  if (slotHeight < fontSize * FONT_LINE_HT) {
    fontSize = Math.max(MIN_FONT, Math.floor(slotHeight / FONT_LINE_HT));
  }

  let tickInterval = 1;
  const neededSlot = fontSize * FONT_LINE_HT;
  if (slotHeight < neededSlot) {
    tickInterval = Math.max(1, Math.ceil(neededSlot / slotHeight));
  }

  const tickValues = tickInterval > 1
    ? labels.filter((_, i) => i % tickInterval === 0)
    : labels;

  const MAX_CHARS_DEFAULT = 20;
  const TRUNCATE_CHARS = 14;
  let tickFormat = (val) => truncateLabel(val, MAX_CHARS_DEFAULT);
  if (truncateLabels) {
    tickFormat = (val) => truncateLabel(val, TRUNCATE_CHARS);
  }

  return { tickValues, tickFormat, fontSize, tickInterval };
}

/** Measure the widest tick label in a category axis. */
export function computeMaxTickLabelWidth(labels, fontSize = 13, fontFamily = 'sans-serif') {
  if (!labels || labels.length === 0) return 0;
  return Math.max(...labels.map((label) => estimateTextWidth(String(label), fontSize, fontFamily)));
}

/** Compute the effective text extent used by axis spacing lanes. */
export function computeTickLabelExtent(labels, typography = {}, rotation = 0, axisSide = 'left') {
  const fontSize = typography.fontSize ?? 13;
  const fontFamily = typography.fontFamily ?? 'sans-serif';
  const maxWidth = computeMaxTickLabelWidth(labels, fontSize, fontFamily);
  const baseHeight = fontSize;
  const angle = Math.abs(rotation || 0) * (Math.PI / 180);
  if (axisSide === 'bottom' || axisSide === 'top') {
    const projectedHeight = Math.abs(maxWidth * Math.sin(angle)) + Math.abs(baseHeight * Math.cos(angle));
    return { width: maxWidth, height: Math.max(baseHeight, projectedHeight) };
  }
  return { width: maxWidth, height: baseHeight };
}

/** Axis title lane thickness from font size. */
export function computeTitleThickness(titleFontSize = 15) {
  return Math.max(6, titleFontSize * 0.5);
}

/** Solve axis lanes from spine -> ticks -> labels -> title. */
export function computeAxisLanes({
  axisSide = 'left',
  labels = [],
  tickLabelExtent,
  rotation = 0,
  tickFontSize = 13,
  tickFontFamily = 'sans-serif',
  titleFontSize = 15,
  showTicks = true,
  availableMargin,
  spacingSpec = {},
  telemetryMeta = {},
}) {
  const isHorizontalAxis = axisSide === 'bottom' || axisSide === 'top';
  const gap = isHorizontalAxis ? (spacingSpec.gapX ?? spacingSpec.xTickGap ?? 8) : (spacingSpec.gapY ?? spacingSpec.yTickGap ?? 7);
  const tickLabelLanePad = isHorizontalAxis
    ? (spacingSpec.tickLabelLanePadX ?? 0)
    : (spacingSpec.tickLabelLanePadY ?? 0);
  const titleGapScale = spacingSpec.titleGapScale ?? 1;
  const tickLength = showTicks
    ? (isHorizontalAxis ? (spacingSpec.xTickLength ?? 4) : (spacingSpec.yTickLength ?? 4))
    : 0;
  const minTitleOffset = spacingSpec.minTitleOffset ?? 16;
  const titleMinInset = spacingSpec.titleMinInset ?? 10;
  const extent = tickLabelExtent || computeTickLabelExtent(
    labels,
    { fontSize: tickFontSize, fontFamily: tickFontFamily },
    rotation,
    axisSide,
  );
  const tickLabelLaneExtent = (isHorizontalAxis ? extent.height : extent.width) + tickLabelLanePad;
  const titleHalf = computeTitleThickness(titleFontSize);
  const tickLabelOffsetPx = tickLength + gap;
  const scaledGap = gap * titleGapScale;
  const rawTitleOffset = tickLabelOffsetPx + tickLabelLaneExtent + scaledGap + titleHalf;
  let titleOffsetPx = rawTitleOffset;
  let clamped = false;
  if (typeof availableMargin === 'number' && Number.isFinite(availableMargin)) {
    const maxOffset = Math.max(minTitleOffset, availableMargin - titleMinInset);
    if (titleOffsetPx > maxOffset) {
      titleOffsetPx = maxOffset;
      clamped = true;
    }
  }
  titleOffsetPx = Math.round(Math.max(minTitleOffset, titleOffsetPx));
  const requiredMarginPx = Math.round(titleOffsetPx + titleHalf + titleMinInset);
  return {
    chartId: telemetryMeta.chartId,
    tickLabelOffsetPx: Math.round(tickLabelOffsetPx),
    tickLabelLaneExtent: Math.round(tickLabelLaneExtent),
    axisSide,
    rawTitleOffsetPx: Math.round(rawTitleOffset),
    titleOffsetPx,
    requiredMarginPx,
    availableMargin,
    clamped,
    showTicks,
    viewportWidth: telemetryMeta.viewportWidth,
    viewportHeight: telemetryMeta.viewportHeight,
  };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function computeYAxisTitleOffset({
  leftMargin,
  tickLabelMaxWidth,
  tickFontSize = 13,
  titleFontSize = 15,
  minGap = 8,
  maxGap = 28,
  minInset = 10,
  showTicks = true,
  tickLength = 4,
  spacingSpec = {},
  telemetryMeta,
}) {
  const resolvedSpacing = {
    ...spacingSpec,
    gapY: clamp(minGap, 0, maxGap),
    yTickLength: tickLength,
    minTitleOffset: spacingSpec.minTitleOffset ?? 16,
    titleMinInset: spacingSpec.titleMinInset ?? minInset,
  };
  const lanes = computeAxisLanes({
    axisSide: 'left',
    tickLabelExtent: { width: Math.max(0, tickLabelMaxWidth || 0), height: tickFontSize },
    tickFontSize,
    titleFontSize,
    showTicks,
    availableMargin: leftMargin,
    spacingSpec: resolvedSpacing,
    telemetryMeta,
  });
  return lanes.titleOffsetPx;
}

export function computeXAxisTitleOffset({
  tickLabelGap = 10,
  tickFontSize = 13,
  titleFontSize = 15,
  labels = [],
  rotation = 0,
  showTicks = true,
  tickLength = 4,
  spacingSpec = {},
  telemetryMeta,
}) {
  const resolvedSpacing = {
    ...spacingSpec,
    gapX: Math.max(0, tickLabelGap || 0),
    xTickLength: tickLength,
    minTitleOffset: spacingSpec.minTitleOffset ?? 16,
    titleMinInset: spacingSpec.titleMinInset ?? 10,
  };
  const lanes = computeAxisLanes({
    axisSide: 'bottom',
    labels,
    rotation,
    tickFontSize,
    titleFontSize,
    showTicks,
    spacingSpec: resolvedSpacing,
    telemetryMeta,
  });
  return lanes.titleOffsetPx;
}
